import { graphql, useMutation } from "react-relay";
import { useToast } from "src/hooks/use-toast";

import type { useSubmitLlmChatFeedbackMutationSubmitChatFeedbackMutation } from "./__generated__/useSubmitLlmChatFeedbackMutationSubmitChatFeedbackMutation.graphql";

// Mutation for submitting chat feedback for LlmConversation (V2)
const submitLlmChatFeedbackMutation = graphql`
  mutation useSubmitLlmChatFeedbackMutationSubmitChatFeedbackMutation($input: SubmitChatFeedbackInput!) {
    submitChatFeedback(input: $input) {
      success
      v1MessageId
      v1ConversationId
      v2MessageId
      v2ConversationId
    }
  }
`;

export type FeedbackType = "THUMBS_UP" | "THUMBS_DOWN";

const FEEDBACK_STORAGE_PREFIX = "chat-feedback";

const buildFeedbackKey = (conversationId: string, messageId: string) =>
  `${FEEDBACK_STORAGE_PREFIX}-${conversationId}-${messageId}`;

const writeFeedback = (conversationId: string, messageId: string, feedback: FeedbackType) => {
  try {
    localStorage.setItem(buildFeedbackKey(conversationId, messageId), feedback);
  } catch {
    // Ignore storage failures
  }
};

export const useSubmitLlmChatFeedbackMutation = () => {
  const { toast } = useToast();
  const [commitSubmitFeedback, isInFlight] = useMutation<useSubmitLlmChatFeedbackMutationSubmitChatFeedbackMutation>(submitLlmChatFeedbackMutation);

  const handleSubmitFeedback = (
    messageId: string,
    conversationId: string,
    feedbackType: FeedbackType,
    feedbackText?: string,
    feedbackTags?: string[],
    onSuccess?: () => void
  ) => {
    // Using GraphQLFormattedMessage fields
    commitSubmitFeedback({
      variables: {
        input: {
          messageId,
          conversationId,
          feedbackType,
          ...(feedbackText && { feedbackText }),
          ...(feedbackTags && feedbackTags.length > 0 && { feedbackTags }),
        }
      },
      onCompleted: (response) => {
        // Update localStorage with all matched IDs for cross-route feedback sync
        const { v1MessageId, v1ConversationId, v2MessageId, v2ConversationId } = response.submitChatFeedback;

        if (v1MessageId && v1ConversationId) {
          writeFeedback(v1ConversationId, v1MessageId, feedbackType);
        }
        if (v2MessageId && v2ConversationId) {
          writeFeedback(v2ConversationId, v2MessageId, feedbackType);
        }

        onSuccess?.();
        toast({
          title: "Thank you for your feedback!",
          variant: "success",
        });
      },
      onError: (error) => {
        toast({
          title: "Error submitting feedback",
          description: error.message,
          variant: "error",
        });
      }
    });
  };

  return {
    handleSubmitFeedback,
    isSubmittingFeedback: isInFlight
  };
};
